// This function is used to get days in month for calculation
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Get inventory status based on month cover
const getInventoryStatus = (monthCover) => {
  let status = 'New';
  let color = '#000000';
  
  if (monthCover < 0) {
    status = 'Out of Stock';
    color = '#dc2626';
  } else if (monthCover === 0) {
    status = 'New';
    color = '#000000';
  } else if (monthCover < 0.5) {
    status = 'Shortage';
    color = '#ea580c';
  } else if (monthCover < 1) {
    status = 'Fast Moving';
    color = '#dc2626';
  } else if (monthCover <= 3) {
    status = 'Ideal';
    color = '#16a34a';
  } else if (monthCover <= 5) {
    status = 'Slow Moving';
    color = '#ca8a04';
  } else if (monthCover > 5) {
    status = 'Excess';
    color = '#2563eb';
  }
  
  return { label: status, color };
};

// Transform raw forecast data to specific view type format
export const transformDataForExport = (forecastData, selectedRegion, selectedMonths, exportViewType, filters = {}) => {
  if (!forecastData || !selectedRegion || !selectedMonths || selectedMonths.length === 0) {
    return [];
  }

  let dataSource;
  switch (exportViewType) {
    case 'category':
      dataSource = forecastData.byCategory?.[selectedRegion] || {};
      break;
    case 'product':
      dataSource = forecastData.byProduct?.[selectedRegion] || {};
      break;
    case 'sku':
    default:
      dataSource = forecastData.bySku?.[selectedRegion] || {};
      break;
  }

  const transformedData = Object.entries(dataSource).map(([key, item]) => {
    const baseData = {
      key,
      currentStock: item.currentStock || 0,
      stockOutDateFormatted: item.stockOutDateFormatted || '-',
    };
    
    // Calculate month cover and stock out date
    const futureMonths = selectedMonths.filter(month => !item.monthData?.[month]?.isPastMonth);
    const totalFutureForecast = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.quantity || 0), 0);
    
    let calculatedMonthCover = 0;
    if (totalFutureForecast > 0 && futureMonths.length > 0) {
      calculatedMonthCover = (item.currentStock || 0) / (totalFutureForecast / futureMonths.length);
      
      const totalDays = futureMonths.reduce((acc, month) => {
        const date = new Date(month);
        return acc + getDaysInMonth(date.getFullYear(), date.getMonth());
      }, 0);
     
      if (totalDays > 0) {
        const dailyForecastRate = totalFutureForecast / totalDays;
        if (dailyForecastRate > 0) {
          // Include upcomingStock in stock-out calculation
          const totalUpcomingStock = futureMonths.reduce((acc, month) => acc + (item.monthData?.[month]?.upcomingTotal || 0), 0);
          const adjustedStock = (item.currentStock || 0) + totalUpcomingStock;
          const daysUntilStockOut = Math.floor(adjustedStock / dailyForecastRate);
        
          if (daysUntilStockOut > 0) {
            const stockOutDate = new Date();
            stockOutDate.setDate(stockOutDate.getDate() + daysUntilStockOut);
            baseData.calculatedStockOutDate = stockOutDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          } else {
            baseData.calculatedStockOutDate = '-';
          }
        } else {
          baseData.calculatedStockOutDate = 'N/A';
        }
      } else {
        baseData.calculatedStockOutDate = 'N/A';
      }
    } else {
      baseData.calculatedStockOutDate = 'N/A';
    }
    
    baseData.calculatedMonthCover = calculatedMonthCover.toFixed(2);
    baseData.inventoryStatus = getInventoryStatus(calculatedMonthCover);
    
    // Add monthly data
    selectedMonths.forEach(month => {
      const monthData = item.monthData?.[month] || {};
      baseData[`${month}_forecast`] = monthData.quantity || 0;
      baseData[`${month}_actual`] = monthData.actualQuantity ?? null;
      baseData[`${month}_mtdSales`] = monthData.mtdSales ?? null;
      baseData[`${month}_mtdDaysPassed`] = monthData.mtdDaysPassed ?? null;
      
      if (monthData.actualQuantity != null && monthData.quantity) {
        const accuracy = 100 - Math.abs((monthData.actualQuantity - monthData.quantity) / monthData.quantity * 100);
        baseData[`${month}_accuracy`] = accuracy > 0 ? accuracy.toFixed(1) : 0;
      } else {
        baseData[`${month}_accuracy`] = null;
      }
      
      baseData[`${month}_upcoming`] = monthData.upcomingTotal || 0;
      baseData[`${month}_upcomingDetails`] = monthData.upcomingDetails || [];
    });
    
    // Add view-specific fields
    switch(exportViewType) {
      case 'category':
        baseData.name = item.categoryName;
        break;
      case 'product':
        baseData.name = item.productName;
        baseData.productName = item.productName;
        baseData.category = item.category;
        break;
      case 'sku':
        baseData.sku = key;
        baseData.productName = item.productName;
        baseData.category = item.category;
        baseData.name = key; // For consistency with existing sort logic
        break;
    }
    
    return baseData;
  });

  // Apply filters if provided
  let filtered = [...transformedData];

  if (filters.searchTerm) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(item => 
      item.sku?.toLowerCase().includes(searchLower) ||
      item.name?.toLowerCase().includes(searchLower) ||
      item.productName?.toLowerCase().includes(searchLower) ||
      item.category?.toLowerCase().includes(searchLower)
    );
  }

  if (filters.statusFilter) {
    filtered = filtered.filter(item => item.inventoryStatus?.label === filters.statusFilter);
  }

  if (filters.categoryFilter && (exportViewType === 'product' || exportViewType === 'sku')) {
    filtered = filtered.filter(item => item.category === filters.categoryFilter);
  }

  return filtered;
}; 
